import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { NanoID } from '../../../../../../../types';
import {
  UniversalFilterCompanyNanoIdValue,
  UniversalFilterCompanyNanoIdModes,
  UniversalFilterCompanyNanoIdFilterState,
  UniversalFilterCompanyNanoIdOnFilterStateChange,
  UniversalFilterCompanyNanoIdOnChange
} from '../../UniversalFilterCompanyNanoId.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCompanyNanoIdFilterState } from '../useUniversalFilterCompanyNanoIdFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterCompanyNanoIdModes.CONTAINS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterCompanyNanoIdModes>[] =
  [
    {
      value: UniversalFilterCompanyNanoIdModes.CONTAINS_ANY_OF,
      i18nText: filtersKeys.modes.containsAnyOf
    }
  ];

interface UniversalFilterCompanyNanoIdOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCompanyNanoIdValue;
  onChange: UniversalFilterCompanyNanoIdOnChange;
}

function useUniversalFilterCompanyNanoId({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCompanyNanoIdOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCompanyNanoIdFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCompanyNanoIdFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCompanyNanoIdFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          companyNanoIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCompanyNanoIdOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterCompanyNanoIdModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        companyNanoIds: filterState.companyNanoIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleCompanyIdsChange = useCallback<
    (companyNanoIds: NanoID[]) => void
  >(
    (companyNanoIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        companyNanoIds: companyNanoIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleCompanyIdsChange
  };
}

export default useUniversalFilterCompanyNanoId;
